import { type ComponentPropsWithoutRef, useMemo } from "react";
import Markdown from "react-markdown";
import rehypeRaw from "rehype-raw";
import remarkBreaks from "remark-breaks";
import remarkGfm from "remark-gfm";
import { cn } from "~/lib/utils";
import {
  TERMS_PROSE_CLASS,
  ensureBoldHeadingSpacing,
  ensureMarkdownNewlines,
  normalizeLiteralNewlines,
  unwrapAllCodeBlocks,
} from "~/utils/markdownTerms";

/**
 * Shared heading component overrides for consistent heading styles
 * across reports, summaries, and modals.
 */
const headingComponents: ComponentPropsWithoutRef<
  typeof Markdown
>["components"] = {
  h1: (props) => (
    <h1 {...props} className="text-lg font-medium mb-2 normal-case" />
  ),
  h2: (props) => <h2 {...props} className="text-base font-bold mb-1" />,
  h3: (props) => (
    <h3 {...props} className="text-sm font-medium uppercase mb-1" />
  ),
};

type UnifiedMarkdownProps = {
  children: string;
  /** Extra className merged with TERMS_PROSE_CLASS */
  className?: string;
  /** Apply custom heading styles (h1, h2, h3). @default false */
  withHeadings?: boolean;
  /** Additional rehype plugins prepended before rehype-raw */
  extraRehypePlugins?: any[];
  /** Additional component overrides (merged after heading components if enabled) */
  components?: ComponentPropsWithoutRef<typeof Markdown>["components"];
};

export const UnifiedMarkdown = ({
  children,
  className,
  withHeadings = false,
  extraRehypePlugins = [],
  components,
}: UnifiedMarkdownProps) => {
  const rehypePlugins = [...extraRehypePlugins, rehypeRaw];

  const preprocessed = useMemo(
    () =>
      ensureBoldHeadingSpacing(
        ensureMarkdownNewlines(
          unwrapAllCodeBlocks(normalizeLiteralNewlines(children))
        )
      ),
    [children]
  );

  return (
    <Markdown
      remarkPlugins={[remarkGfm, remarkBreaks]}
      rehypePlugins={rehypePlugins}
      className={cn(TERMS_PROSE_CLASS, className)}
      components={{
        ...(withHeadings ? headingComponents : {}),
        ...components,
      }}
    >
      {preprocessed}
    </Markdown>
  );
};
